// Phrases for controller instructions, pilot read-backs and pilot calls (ICAO Doc 4444 Chapter 12, Doc 9432,
// German RTF procedures). Every phrase exists twice: as written in the radio log and as spoken by the TTS.
import type { Cmd, TaxiTarget } from '../sim/commands';
import type { FlightPlan } from '../sim/flightplan';
import { sayCallsign } from '../sim/flightplan';
import { sayFreq, sayHeading, sayLevel, showLevel, saySpeed, sayRunway, saySquawk, sayWind, spell, sayQnh, ALPHABET } from './spoken';

export interface PhraseCtx {
  unitCallsign(unitId: string): string;
  unitFreq(unitId: string): string;
  /** Spoken name of a destination ('Munich'). */
  destName(icao: string): string;
  /** Spoken name of a fix. */
  fixSay(name: string): string;
  standSay(id: string): string;
  /** Surface wind for take-off/landing clearances: magnetic direction. */
  windFor(rwy: string): { dir: number; kt: number; gust?: number };
  qnh: number;
  /** Current pressure/QNH altitude of the addressee, to choose climb or descend. */
  currentAlt(cs: string): number | null;
  /** Current indicated airspeed of the addressee, to choose reduce or increase. */
  currentSpeed(cs: string): number | null;
}

export interface Phrase { text: string; spoken: string }
const P = (text: string, spoken = text): Phrase => ({ text, spoken });
const join = (ps: Phrase[], sep = ', '): Phrase => ({ text: ps.map(p => p.text).join(sep), spoken: ps.map(p => p.spoken).join(sep) });

/** 'NASUK 1Z' → 'Nasuk one Zulu'. */
export function saySid(name: string, ctx: Pick<PhraseCtx, 'fixSay'>): string {
  const m = /^([A-Z]{2,5})\s*(\d)([A-Z])$/.exec(name.trim());
  if (!m) return name;
  return `${ctx.fixSay(m[1]!)} ${spell(m[2]!)} ${ALPHABET[m[3]!]}`;
}
const viaText = (via: string[]) => (via.length ? ` via ${via.join(', ')}` : '');
const viaSay = (via: string[]) => (via.length ? ` via ${via.map(v => spell(v)).join(', ')}` : '');
export const sayHold = (h: string): string => spell(h);

function taxiTarget(t: TaxiTarget, ctx: PhraseCtx): Phrase {
  if (t.kind === 'HOLD') return P(`taxi to holding point ${t.hold} runway ${t.rwy}`, `taxi to holding point ${sayHold(t.hold)} runway ${sayRunway(t.rwy)}`);
  if (t.kind === 'STAND') { const s = ctx.standSay(t.stand); return P(`taxi to ${s.replace(/stand (\S+)/, 'stand $1')}`, `taxi to ${s.replace(/(\d+)/g, d => spell(d))}`); }
  return P(`taxi to ${t.label}`, `taxi to ${t.label}`);
}

function windPhrase(rwy: string, ctx: PhraseCtx): Phrase {
  const w = ctx.windFor(rwy);
  const text = w.kt < 1 ? 'wind calm' : `wind ${String(Math.round(w.dir / 10) * 10 || 360).padStart(3, '0')} degrees ${Math.round(w.kt)} knots${w.gust ? ` gusting ${w.gust} knots` : ''}`;
  return P(text, sayWind(w.dir, w.kt, w.gust));
}

/** One controller instruction. */
export function cmdPhrase(c: Cmd, cs: string, ctx: PhraseCtx): Phrase {
  switch (c.k) {
    case 'CLEARANCE': return P(
      `cleared to ${ctx.destName(c.dest)} via ${c.sid} departure, climb initially ${showLevel(c.altFt, false)}, squawk ${c.squawk}`,
      `cleared to ${ctx.destName(c.dest)} via ${saySid(c.sid, ctx)} departure, climb initially ${sayLevel(c.altFt, false)}, squawk ${saySquawk(c.squawk)}`);
    case 'RB_OK': return P('readback correct');
    case 'PUSH': return P(`push and start-up approved${c.facing ? `, facing ${c.facing}` : ''}`);
    case 'STARTUP': return P('start-up approved');
    case 'TAXI': {
      const t = taxiTarget(c.target, ctx);
      return P(`${t.text}${viaText(c.via)}${c.holdShort ? `, hold short of runway ${c.holdShort}` : ''}`,
        `${t.spoken}${viaSay(c.via)}${c.holdShort ? `, hold short of runway ${sayRunway(c.holdShort)}` : ''}`);
    }
    case 'HOLD_POS': return P('hold position');
    case 'CONT_TAXI': return P('continue taxi');
    case 'CROSS': return P(`cross runway ${c.rwy}`, `cross runway ${sayRunway(c.rwy)}`);
    case 'HOLD_SHORT': return P(`hold short of runway ${c.rwy}`, `hold short of runway ${sayRunway(c.rwy)}`);
    case 'LINEUP': {
      const inter = c.hold ? `, intersection ${c.hold}` : '';
      const beh = c.behind ? `behind ${c.behind}, ` : '';
      return P(`${beh}line up and wait runway ${c.rwy}${inter}`, `${beh}line up and wait runway ${sayRunway(c.rwy)}${c.hold ? `, intersection ${sayHold(c.hold)}` : ''}`);
    }
    case 'TAKEOFF': return join([...(c.wind === false ? [] : [windPhrase(c.rwy, ctx)]), P(`runway ${c.rwy}, cleared for take-off`, `runway ${sayRunway(c.rwy)}, cleared for take-off`)]);
    case 'CANCEL_TAKEOFF': return P('hold position, cancel take-off, I say again, cancel take-off');
    case 'LAND': return join([...(c.wind === false ? [] : [windPhrase(c.rwy, ctx)]), P(`runway ${c.rwy}, cleared to land`, `runway ${sayRunway(c.rwy)}, cleared to land`)]);
    case 'CONTINUE': return P('continue approach');
    case 'GO_AROUND': return P('go around, I say again, go around');
    case 'VACATE': return P(`vacate via ${c.via}`, `vacate via ${sayHold(c.via)}`);
    case 'HDG': {
      const h = String(Math.round(c.hdg) % 360 || 360).padStart(3, '0');
      const verb = c.turn === 'L' ? 'turn left heading' : c.turn === 'R' ? 'turn right heading' : 'fly heading';
      return P(`${verb} ${h}`, `${verb} ${sayHeading(c.hdg)}`);
    }
    case 'ALT': {
      const cur = ctx.currentAlt(cs);
      const verb = cur === null ? 'maintain' : c.ft > cur + 50 ? 'climb to' : c.ft < cur - 50 ? 'descend to' : 'maintain';
      const withQnh = !c.fl && verb === 'descend to';
      return P(`${verb} ${c.fl ? showLevel(c.ft, true) : `altitude ${showLevel(c.ft, false)}`}${withQnh ? `, QNH ${ctx.qnh}` : ''}`,
        `${verb} ${c.fl ? sayLevel(c.ft, true) : `altitude ${sayLevel(c.ft, false)}`}${withQnh ? `, ${sayQnh(ctx.qnh)}` : ''}`);
    }
    case 'SPD': {
      const cur = ctx.currentSpeed(cs);
      const verb = cur === null || Math.abs(c.kt - cur) < 5 ? 'maintain' : c.kt < cur ? 'reduce speed to' : 'increase speed to';
      return P(`${verb} ${Math.round(c.kt)} knots`, `${verb} ${saySpeed(c.kt)} knots`);
    }
    case 'SPD_OWN': return P('no speed restriction');
    case 'DIRECT': return P(`proceed direct ${c.fix}`, `proceed direct ${ctx.fixSay(c.fix)}`);
    case 'ILS': return P(`cleared ILS approach runway ${c.rwy}`, `cleared I L S approach runway ${sayRunway(c.rwy)}`);
    case 'HOLD_AT': return P(`hold at ${c.fix} as published`, `hold at ${ctx.fixSay(c.fix)} as published`);
    case 'CONTACT': {
      const f = ctx.unitFreq(c.unit);
      return P(`contact ${ctx.unitCallsign(c.unit)} ${f}`, `contact ${ctx.unitCallsign(c.unit)} ${sayFreq(f)}`);
    }
    case 'SQUAWK': return P(`squawk ${c.code}`, `squawk ${saySquawk(c.code)}`);
    case 'IDENTIFIED': return P('identified');
    case 'SAY_AGAIN': return P('say again');
    case 'REPORT_EST': return P('report established');
    case 'RESUME_OWN': return P('resume own navigation');
  }
}

export function atcTransmission(fp: FlightPlan, cmds: Cmd[], ctx: PhraseCtx, station?: string): Phrase {
  const cs = P(fp.callsign, sayCallsign(fp));
  const parts = [cs, ...(station ? [P(station)] : []), ...cmds.map(c => cmdPhrase(c, fp.callsign, ctx))];
  return join(parts);
}

/** Read-back of one instruction. Returns null if nothing has to be read back. */
export function readbackPhrase(c: Cmd, ctx: PhraseCtx, cs: string): Phrase | null {
  switch (c.k) {
    case 'RB_OK': case 'IDENTIFIED': case 'SAY_AGAIN': return null;
    case 'TAKEOFF': return P(`cleared for take-off runway ${c.rwy}`, `cleared for take-off runway ${sayRunway(c.rwy)}`);
    case 'LAND': return P(`cleared to land runway ${c.rwy}`, `cleared to land runway ${sayRunway(c.rwy)}`);
    case 'CONTACT': {
      const f = ctx.unitFreq(c.unit);
      return P(`${ctx.unitCallsign(c.unit).split(' ').slice(-1)[0]} ${f}`, `${ctx.unitCallsign(c.unit).split(' ').slice(-1)[0]} ${sayFreq(f)}`);
    }
    case 'GO_AROUND': return P('going around');
    case 'CLEARANCE': {
      const p = cmdPhrase(c, cs, ctx);
      return P(p.text.replace(/^cleared to /, 'cleared to '), p.spoken);
    }
    default: return cmdPhrase(c, cs, ctx);
  }
}

export function readbackTransmission(fp: FlightPlan, cmds: Cmd[], ctx: PhraseCtx): Phrase | null {
  const parts = cmds.map(c => readbackPhrase(c, ctx, fp.callsign)).filter((p): p is Phrase => !!p);
  if (!parts.length) return null;
  return join([...parts, P(fp.callsign, sayCallsign(fp, !fp.telephony))]);
}

/** Pilot-initiated call: "<station>, <callsign>, <message>". */
export function pilotCall(fp: FlightPlan, station: string | null, msg: Phrase): Phrase {
  return join([...(station ? [P(station)] : []), P(fp.callsign, sayCallsign(fp)), msg]);
}
export { P as phrase, join as joinPhrases };
